import {
	Form,
	FormItem,
	ReorderableList
} from "client/Components";
import { AppDestinationWindowSelectFormItem } from "client/Features/Apps/AppDestinationWindowSelectFormItem";
import {
	AddTilesResponseModel,
	EmailUpdateTileModel,
	EmailAppDetailsTileModel,
	EmailAppTabDetailsModel,
	UpdateEmailAppTabModel,
	updateEmailTile
} from "client/Api/Workspace/workspace";
import { IFormHandler, useFormHandler } from "client/Forms";
import { IValueInputAttachable } from "client/Forms/attachable";
import { generateTrackingId, isNotNil } from "client/Misc/utils";
import { createValidationConfiguration, IValidationConfiguration } from "client/Validation";
import {
	doesNotExceedLength,
	isEmailAddress,
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import {
	forwardRef,
	ReactElement,
	Ref,
	useCallback,
	useImperativeHandle,
	useMemo,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import { newGuid } from "client_legacy/ndaw/guid";
import {
	ContentKind,
	FolderIdentifier,
	FolderIdentifierKind,
	FolderIdentifierModel,
	WellKnownFolder,
	WellKnownFolderIdentifierModel
} from "client/Api/Workspace/LiveInformation/Email/emailLiveInformation";
import { ExistingTile } from "../common";
import { EditTileFromData, initialGenericState } from "../EditGenericTile";
import { EditTileHandle } from "../EditTile";
import { EditTileGeneralFields } from "../GeneralFields";
import { useUpdateTile } from "../hooks/useUpdateTile";
import { EditEmailTabItem } from "./EditEmailTabItem";

const MAX_TAB_NUMBER: number = 20;


type WellKnownFolders = {
	[key in WellKnownFolder]: WellKnownFolderIdentifierModel;
};

export const getWellKnownFolderName = (wellKnownFolderIdentifier: WellKnownFolderIdentifierModel): string => {
	switch(wellKnownFolderIdentifier.folder){
		case WellKnownFolder.Inbox:
			return localize("_Inbox");
		default:
			never(wellKnownFolderIdentifier.folder);
	}
};

export const WELLKNOWN_FOLDERS: WellKnownFolders = {
	[WellKnownFolder.Inbox]: {
		folder: WellKnownFolder.Inbox
	}
} as const;

type EmailEditFormData = EditTileFromData<EmailUpdateTileModel>;

const validationBuilder: IValidationConfiguration<EmailEditFormData> =
	createValidationConfiguration<EmailEditFormData>();

validationBuilder.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);

const tabValidation = validationBuilder.when(
	(formData): boolean =>	{
		const { columnSpan, rowSpan } = formData.dimension;
		return computeAreTabNeeded(columnSpan, rowSpan);
	}
)
	.forCollection("tabs")
	.forItems(
		item => item.uniqueId
	);

tabValidation.forObject("user")
	.whenType(isNotNil)
	.forObject("folder")
	.add(
		isNotNullNorUndefined()
	);

tabValidation.forObject("personal")
	.whenType(isNotNil)
	.forObject("folder")
	.add(
		isNotNullNorUndefined()
	);

tabValidation.forObject("shared")
	.whenType(isNotNil)
	.forObject("folder")
	.add(
		isNotNullNorUndefined()
	);

const otherMailboxValidation = tabValidation.forObject("other")
	.whenType(isNotNil);

otherMailboxValidation.forObject("folder")
	.add(
		isNotNullNorUndefined()
	);

otherMailboxValidation.forObject("mailbox")
	.forScalar("smtpAddress")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		isEmailAddress()
	);

tabValidation.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

export type EditEmailTileProps = {
	initialModel: EmailAppDetailsTileModel;
	mode: ExistingTile;
	onIsLoadingChange(isLoading: boolean): void;
	isLoading: boolean;
	onAfterSave: (response?: AddTilesResponseModel) => void;
};

export const EditEmailTile = forwardRef(
	(props: EditEmailTileProps, ref: Ref<EditTileHandle>): ReactElement => {
		const {
			initialModel,
			mode,
			onIsLoadingChange,
			isLoading,
			onAfterSave,
		} = props;

		const [formData, setFormData] = useState<EmailEditFormData>({
			...initialGenericState(initialModel),
			destinationWindow: initialModel.destinationWindow,
			tabs: initialModel.tabs
				.map(
					(tab: EmailAppTabDetailsModel): UpdateEmailAppTabModel => {
						return convertToUpdateTabModel(tab);
					}
				)
		});

		const formHandler: IFormHandler<EmailEditFormData> = useFormHandler(
			formData,
			setFormData,
			validationBuilder
		);

		const { columnSpan, rowSpan } = formHandler.for("dimension")
			.value;

		const areTabsNeeded: boolean = useMemo(
			(): boolean => {
				return computeAreTabNeeded(columnSpan, rowSpan);
			},
			[columnSpan, rowSpan]
		);

		const { updateHandler } = useUpdateTile<EmailUpdateTileModel>(
			formHandler,
			updateEmailTile,
			isLoading,
			onIsLoadingChange,
			mode,
			onAfterSave,
			prepareModelForSaving
		);

		useImperativeHandle(
			ref,
			() => ({
				updateHandler
			})
		);

		const itemMoveHandler = useCallback(
			(fromIndex: number, toIndex: number): void => {
				setFormData(
					(formState) => {
						const tabsCopy: UpdateEmailAppTabModel[] | null = [...formState.tabs];
						const item: UpdateEmailAppTabModel = tabsCopy.splice(fromIndex, 1)[0];
						tabsCopy.splice(toIndex, 0, item);

						return {
							...formState,
							tabs: tabsCopy
						};
					}
				);
			},
			[]
		);

		const addTabHandler = (): void => {
			const newTab: UpdateEmailAppTabModel = {
					uniqueId: newGuid(),
					name: "",
					contentKind: ContentKind.Unread,
					personal: {
						folder: {
							wellKnown: WELLKNOWN_FOLDERS[WellKnownFolder.Inbox]
						}
					}
			};

			const tabsHandler = formHandler.for("tabs");

			tabsHandler.reducer
				.push(newTab);

			tabsHandler.validation
				.triggerInputFinished();
		};

		const removeTabHandler = (index: number): void => {
			const tabsHandler: IValueInputAttachable<UpdateEmailAppTabModel[]> = formHandler.for("tabs");

			if (formHandler.for("tabs").value.length > 1) {
				tabsHandler.reducer
					.removeAt(index);

				tabsHandler.validation
					.triggerInputFinished();
			}
		};

		return (
			<Form onSubmit="NONE">
				<EditTileGeneralFields
					initialModel={initialModel}
					formHandler={formHandler}
				/>
				<AppDestinationWindowSelectFormItem
					attachable={formHandler.for("destinationWindow")}
				/>
				{areTabsNeeded && (
					<FormItem hasNoMargin>
						<ReorderableList
							onItemMove={itemMoveHandler}
							trackingId="tabs-items-container"
						>
							{formHandler.for("tabs")
								.map((tabHandler, index) => (
									<EditEmailTabItem
										key={tabHandler.value
											.uniqueId
										}
										sharedMailboxes={initialModel.sharedMailboxes}
										personalMailbox={initialModel.defaultMailboxSmtpAddress}
										isAdmin={initialModel.isAdmin}
										onRemoveTab={() => removeTabHandler(index)}
										attachable={tabHandler}
									/>
								))
							}
						</ReorderableList>
						{formData.tabs.length < MAX_TAB_NUMBER && (
							<ReorderableList.AddButton
								trackingId={generateTrackingId("edit-email-tile", "__add-tab-button")}
								onClick={addTabHandler}
							/>
						)}
					</FormItem>
				)}
			</Form>
		);
	}
);

EditEmailTile.displayName = "EditEmailTile";

function convertToUpdateTabModel(tab: EmailAppTabDetailsModel): UpdateEmailAppTabModel{
	return {
		uniqueId: tab.uniqueId,
		name: tab.name,
		contentKind: tab.contentKind,
		user: tab.user
			? {
				folder: WELLKNOWN_FOLDERS[
					tab.user
						.folder
						.folder
				]
			}
			: undefined,
		personal: tab.personal
			? {
				folder: folderIdentifierToModel(
					tab.personal
						.folder
				)
			}
			: undefined,
		shared: tab.shared
			? {
				mailbox: tab.shared
					.mailbox,
				folder: folderIdentifierToModel(
					tab.shared
						.folder
				)
			}
			: undefined,
		other: tab.other
			? {
				mailbox: tab.other
					.mailbox,
				folder: folderIdentifierToModel(
					tab.other
						.folder
				),
			}
			: undefined,
	};
}

function computeAreTabNeeded(columnSpan: number, rowSpan: number): boolean {
	return columnSpan >= 4 && rowSpan >= 4;
}

function prepareModelForSaving(formData: EmailEditFormData): EmailEditFormData {
	const { columnSpan, rowSpan } = formData.dimension;
	const areTabsNeeded = computeAreTabNeeded(columnSpan, rowSpan);

	return {
		...formData,
		tabs: areTabsNeeded ? formData.tabs : []
	};
}

function folderIdentifierToModel(folder: FolderIdentifier): FolderIdentifierModel {
	switch (folder.kind) {
		case FolderIdentifierKind.WellKnown:
			return {
				wellKnown: WELLKNOWN_FOLDERS[folder.folder]
			};
		case FolderIdentifierKind.Specific:
			return {
				specific: {
					id: folder.id,
					name: folder.name
				}
			};
		default:
			never(folder);
	}
}